import { useEffect, useState } from 'react';
import { useTransactionService } from '../../../services/transaction.service';
import { TaxReportWithYear } from '../../../services/tax-reports.types';

interface UseTransactionCountsProps {
  selectedTaxYears: Record<string, any>;
  taxReports: TaxReportWithYear[] | undefined;
}

interface UseTransactionCountsResult {
  transactionCounts: Record<string, number>;
  isLoadingTransactions: boolean;
}

export const useTransactionCounts = ({
  selectedTaxYears,
  taxReports,
}: UseTransactionCountsProps): UseTransactionCountsResult => {
  const [transactionCounts, setTransactionCounts] = useState<Record<string, number>>({});
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const transactionService = useTransactionService();

  useEffect(() => {
    let isMounted = true;

    const fetchTransactionStats = async () => {
      // Don't fetch if there are no selected tax years
      if (Object.keys(selectedTaxYears).length === 0) return;

      // Don't set loading if we already have the data for all years
      const missingYears = Object.keys(selectedTaxYears).filter(
        (year) => transactionCounts[year] === undefined
      );

      if (missingYears.length === 0) return;

      if (isMounted) setIsLoadingTransactions(true);
      const counts = { ...transactionCounts };

      // Only process if we have tax reports
      if (taxReports && taxReports.length > 0) {
        // Process only years we don't have data for yet
        for (const yearKey of missingYears) {
          const taxYearReport = taxReports.find((report) => report.tax_year === yearKey);
          if (taxYearReport && taxYearReport.id) {
            try {
              // Fetch transaction stats for this year and report ID
              const stats = await transactionService.getStats(yearKey, taxYearReport.id);
              counts[yearKey] = stats.totalTransactions || 0;
            } catch (error) {
              console.error(`Error fetching stats for year ${yearKey}:`, error);
              counts[yearKey] = 0;
            }
          } else {
            counts[yearKey] = 0;
          }
        }

        if (isMounted) setTransactionCounts(counts);
      }
      if (isMounted) setIsLoadingTransactions(false);
    };

    fetchTransactionStats();

    // Cleanup function to prevent state updates if the component unmounts
    return () => {
      isMounted = false;
    };
  }, [selectedTaxYears, taxReports, transactionService, transactionCounts]);

  return {
    transactionCounts,
    isLoadingTransactions,
  };
};
